"use client";

import ButtonArrow from "@/components/ButtonArrow";
import CmsLink from "@/components/CmsLink";
import ProjectShortCard from "@/components/ProjectShortCard";
import { useMemo, useState } from "react";

export type SectionHeading = {
    prefix?: string;
    highlightedText?: string;
    suffix?: string;
};

export type SectionLink = {
    label?: string;
    link?: string;
    target?: "_self" | "_blank";
};

export type WebsiteSelectedCategoryProject = {
    key?: string;
    title?: string;
    image?: { url?: string; alt?: string } | null;
    projectLink?: { label?: string; link?: string };
    caseStudyLink?: { label?: string; link?: string };
    smallDescription?: string;
    type?: "website" | "social-media";
    mediaHandleName?: string;
};

export type WebsiteSelectedCategoryItem = {
    name?: string;
    slug?: string;
    projects?: WebsiteSelectedCategoryProject[];
};

export type WebsiteSelectedCategoryProps = {
    legend?: string[];
    heading?: SectionHeading;
    description?: string;
    categories?: WebsiteSelectedCategoryItem[];
    primaryButton?: SectionLink;
    secondaryButton?: SectionLink;
};

const ALL_SLUG = "all";

const WebsiteSelectedCategory = ({
    legend = [],
    heading,
    description,
    categories = [],
    primaryButton,
    secondaryButton,
}: WebsiteSelectedCategoryProps) => {

    const filteredCategories = categories.filter(
        (category) => category.name?.trim() || category.slug?.trim()
    );

    const [activeSlug, setActiveSlug] = useState(ALL_SLUG);

    const allProjects = useMemo(() => {
        const byKey = new Map<string, WebsiteSelectedCategoryProject>();

        filteredCategories.forEach((category) => {
            (category.projects ?? []).forEach((project, index) => {
                const key =
                    project.key ??
                    `${category.slug ?? category.name ?? "category"}-${project.title ?? index}`;
                if (byKey.has(key)) return;

                byKey.set(key, {
                    ...project,
                    key,
                });
            });
        });

        return [...byKey.values()];
    }, [filteredCategories]);

    const visibleProjects = useMemo(() => {
        if (activeSlug === ALL_SLUG) return allProjects;

        const activeCategory = filteredCategories.find(
            (category) => (category.slug || category.name) === activeSlug
        );

        return (activeCategory?.projects ?? []).map((project, index) => ({
            ...project,
            key:
                project.key ??
                `${activeCategory?.slug ?? activeCategory?.name ?? "category"}-${project.title ?? index}`,
        }));
    }, [activeSlug, allProjects, filteredCategories]);

    const headingText = [heading?.prefix, heading?.highlightedText, heading?.suffix]
        .filter(Boolean)
        .join(" ")
        .trim();

    const hasPrimaryButton = Boolean(
        primaryButton?.label?.trim() || primaryButton?.link?.trim()
    );
    const hasSecondaryButton = Boolean(
        secondaryButton?.label?.trim() || secondaryButton?.link?.trim()
    );
    const eyebrowText = legend.filter(Boolean).join(" · ");

    if (filteredCategories.length === 0 && visibleProjects.length === 0) {
        return null;
    }

    return (
        <section id="Website_selected_category">
            <div className="body_y_padding main_heading_content_space_y space_from_side body_max_width">
                {eyebrowText.length > 0 || headingText || description ? (
                    <div className="main_heading_description_space_y max-w-2xl">
                        {eyebrowText.length > 0 ? (
                            <div className="legend_text w-fit">{eyebrowText}</div>
                        ) : null}
                        {headingText ? (
                            <h2 className="heading_text">
                                {heading?.prefix}{" "}
                                {heading?.highlightedText ? (
                                    <em className="faded_underline">{heading.highlightedText}</em>
                                ) : null}{" "}
                                {heading?.suffix}
                            </h2>
                        ) : null}
                        {description ? <p>{description}</p> : null}
                    </div>
                ) : null}

                <div className="main_heading_content_space_y">
                    <div className="flex flex-wrap gap-5 justify-between items-end">
                        <div className="flex flex-wrap gap-2.5">
                            <button
                                type="button"
                                onClick={() => setActiveSlug(ALL_SLUG)}
                                className={`text-sm font-semibold py-2.5 px-4.5 common_border cursor-pointer ${activeSlug === ALL_SLUG
                                    ? "bg-primary text-white"
                                    : "text-blackishBrown bg-white"
                                    }`}
                            >
                                All
                            </button>
                            {filteredCategories.map((category) => {
                                const slug = category.slug || category.name || "";
                                const isActive = activeSlug === slug;
                                return (
                                    <button
                                        key={slug}
                                        type="button"
                                        onClick={() => setActiveSlug(slug)}
                                        className={`text-sm font-semibold py-2.5 px-4.5 common_border cursor-pointer ${isActive
                                            ? "bg-primary text-white"
                                            : "text-blackishBrown bg-white"
                                            }`}
                                    >
                                        {category.name?.trim() || slug}
                                    </button>
                                );
                            })}
                        </div>
                        {visibleProjects.length ? <p className="text-sm text-secondary font-semibold">
                            {visibleProjects.length} Projects
                        </p>:<></>}
                    </div>

                    {visibleProjects.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5.5">
                            {visibleProjects.map((project, index) => (
                                <ProjectShortCard
                                    key={project.key ?? `${project.title}-${index}`}
                                    title={project.title}
                                    image={project.image}
                                    projectLink={project.projectLink}
                                    caseStudyLink={project.caseStudyLink}
                                    smallDescription={project.smallDescription}
                                    type={project.type}
                                    mediaHandleName={project.mediaHandleName}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-4xl text-center font-bold">
                            No projects in this category yet.
                        </p>
                    )}
                </div>

                {hasPrimaryButton || hasSecondaryButton ? (
                    <div className="flex flex-wrap justify-center gap-3 items-center">
                        {hasSecondaryButton ? (
                            <CmsLink
                                className="btn btn-ghost"
                                href={secondaryButton?.link || "#"}
                                target={secondaryButton?.target ?? "_self"}
                            >
                                {secondaryButton?.label?.trim()}
                            </CmsLink>
                        ) : null}
                        {hasPrimaryButton ? (
                            <CmsLink
                                className="btn btn-primary"
                                href={primaryButton?.link || "#"}
                                target={primaryButton?.target ?? "_self"}
                            >
                                {primaryButton?.label?.trim()} <ButtonArrow />
                            </CmsLink>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default WebsiteSelectedCategory;
